import { useCallback, useEffect, useRef, useState } from "react";
import { printingApi } from "../lib/printing/printingApi";
import { operatorMessage } from "../lib/printing/printingModel";

/**
 * Loads everything the printing dashboard shows. One `reload()` refreshes
 * all of it; `reloadLive()` refreshes only the fast-changing parts
 * (agents, printers, jobs) and runs on a timer while the page is visible.
 * Errors are reduced to operator messages — never raw bodies.
 */
export default function usePrintingData({ pollMs = 10000 } = {}) {
  const [state, setState] = useState({
    loading: true, error: null,
    agents: [], printers: [], profiles: null, config: null, automaticPrinting: null, documents: null,
    layouts: null, sections: null, rules: null, jobs: [], catalogs: [],
  });
  const alive = useRef(true);

  const settle = useCallback((patch) => { if (alive.current) setState((s) => ({ ...s, ...patch })); }, []);

  const reload = useCallback(async () => {
    settle({ loading: true, error: null });
    try {
      const [agents, printers, profiles, config, documents, layouts, sections, rules, jobs, catalogs] = await Promise.all([
        printingApi.agents(), printingApi.printers(), printingApi.profiles(), printingApi.config(),
        printingApi.documents(), printingApi.layouts(null, true), printingApi.layoutSections(), printingApi.rules(),
        printingApi.jobs(), printingApi.catalogs().catch(() => ({ catalogs: [] })),
      ]);
      settle({
        loading: false, error: null,
        agents: agents.agents || [], printers: printers.printers || [], profiles, config: config.config || null,
        automaticPrinting: config.automatic_printing || null,
        documents, layouts, sections, rules, jobs: jobs.jobs || [], catalogs: catalogs.catalogs || [],
      });
    } catch (e) {
      settle({ loading: false, error: operatorMessage(e, "Could not load printing settings.") });
    }
  }, [settle]);

  const reloadLive = useCallback(async () => {
    try {
      const [agents, printers, jobs] = await Promise.all([printingApi.agents(), printingApi.printers(), printingApi.jobs()]);
      settle({ agents: agents.agents || [], printers: printers.printers || [], jobs: jobs.jobs || [] });
    } catch (_e) { /* keep the last good data; the next full reload reports */ }
  }, [settle]);

  const reloadConfig = useCallback(async () => {
    try {
      const [config, documents, rules, layouts] = await Promise.all([
        printingApi.config(), printingApi.documents(), printingApi.rules(), printingApi.layouts(null, true),
      ]);
      settle({ config: config.config || null, automaticPrinting: config.automatic_printing || null, documents, rules, layouts });
    } catch (e) {
      settle({ error: operatorMessage(e, "Could not refresh printing settings.") });
    }
  }, [settle]);

  useEffect(() => {
    alive.current = true;
    reload();
    const timer = pollMs > 0 ? setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") reloadLive();
    }, pollMs) : null;
    return () => { alive.current = false; if (timer) clearInterval(timer); };
  }, [reload, reloadLive, pollMs]);

  return { ...state, reload, reloadLive, reloadConfig };
}
